import React from 'react';
import { Table, Form } from 'react-bootstrap';
import { useState, useEffect, useMemo, useRef } from 'react';
import ExcelExportButton from './ExcelExportButton';
import { chosen, reportFileName, reportMeta } from '../helpers/TableExcelExport';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from '../helpers/Fetchwrapper';
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';
import { useLocation } from 'react-router-dom';

interface DailySalesCustomerRow {
    quantity: number;
    unit_name: string;
    total_ltr: number;
    invoice_price: number;
    total_amount: number;
    free_product_quantity: number;
    free_product_unit: string;
    customer_name: string;
}

interface DailySalesSrGroup {
    sr_name: string;
    customers: DailySalesCustomerRow[];
    row_count: number;
}

interface DailySalesProductGroup {
    product_name: string;
    pack_size: string;
    srs: DailySalesSrGroup[];
    row_count: number;
}

interface DailySalesDateGroup {
    products: DailySalesProductGroup[];
    total_amount: number;
    total_ltr: number;
    row_count: number;
}

interface DailySalesData {
    dates: {
        [date: string]: DailySalesDateGroup;
    };
    grand_total_amount: number;
    grand_total_ltr: number;
}

interface SearchFilters {
    date: string;
    productName: string;
    packSize: string;
    sr: string;
    customerName: string;
}

const emptySearchFilters: SearchFilters = {
    date: '',
    productName: '',
    packSize: '',
    sr: '',
    customerName: '',
};

const matchesFilter = (value: string, filter: string) => {
    if (!filter.trim()) {
        return true;
    }

    return value.toLowerCase().includes(filter.trim().toLowerCase());
};

const filterSalesData = (data: DailySalesData, filters: SearchFilters): DailySalesData => {
    const filteredDates: DailySalesData['dates'] = {};
    let grandTotalAmount = 0;
    let grandTotalLtr = 0;

    Object.entries(data.dates).forEach(([date, dateGroup]) => {
        if (!matchesFilter(date, filters.date)) {
            return;
        }

        const filteredProducts: DailySalesProductGroup[] = [];

        dateGroup.products.forEach((product) => {
            if (!matchesFilter(product.product_name, filters.productName)) {
                return;
            }

            if (!matchesFilter(String(product.pack_size), filters.packSize)) {
                return;
            }

            const filteredSrs: DailySalesSrGroup[] = [];

            product.srs.forEach((sr) => {
                if (!matchesFilter(sr.sr_name, filters.sr)) {
                    return;
                }

                const filteredCustomers = sr.customers.filter((customer) =>
                    matchesFilter(customer.customer_name, filters.customerName)
                );

                if (filteredCustomers.length === 0) {
                    return;
                }

                filteredSrs.push({
                    ...sr,
                    customers: filteredCustomers,
                    row_count: filteredCustomers.length,
                });
            });

            if (filteredSrs.length === 0) {
                return;
            }

            const productRowCount = filteredSrs.reduce((sum, sr) => sum + sr.row_count, 0);

            filteredProducts.push({
                ...product,
                srs: filteredSrs,
                row_count: productRowCount,
            });
        });

        if (filteredProducts.length === 0) {
            return;
        }

        let totalAmount = 0;
        let totalLtr = 0;

        filteredProducts.forEach((product) => {
            product.srs.forEach((sr) => {
                sr.customers.forEach((customer) => {
                    totalAmount += customer.total_amount;
                    totalLtr += customer.total_ltr;
                });
            });
        });

        const dateRowCount = filteredProducts.reduce((sum, product) => sum + product.row_count, 0);

        grandTotalAmount += totalAmount;
        grandTotalLtr += totalLtr;

        filteredDates[date] = {
            products: filteredProducts,
            total_amount: totalAmount,
            total_ltr: totalLtr,
            row_count: dateRowCount,
        };
    });

    return {
        dates: filteredDates,
        grand_total_amount: grandTotalAmount,
        grand_total_ltr: grandTotalLtr,
    };
};

const DailySalesReport: React.FC = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);

    const [loading, setLoading] = useState(false);
    const [salesData, setSalesData] = useState<DailySalesData | null>(null);
    const reportRef = useRef<HTMLDivElement>(null);
    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        idSrCategories: query.get('idSrCategories') ?? '',
        fromDate: query.get('fromDate') ?? '',
        toDate: query.get('toDate') ?? '',
    });

    const [projects, setProjects] = useState<{ label: string; value: string }[]>([]);
    const [project, setProject] = useState<{ label: string; value: string }>({ label: 'Select Project', value: '' });
    const [categories, setCategories] = useState<{ label: string; value: string; id?: any; name?: string }[]>([]);
    const [category, setCategory] = useState<{ label: string; value: string }>({ label: 'Select SR Category', value: '' });
    const [searchFilters, setSearchFilters] = useState<SearchFilters>(emptySearchFilters);

    const filteredSalesData = useMemo(() => {
        if (!salesData) {
            return null;
        }

        return filterSalesData(salesData, searchFilters);
    }, [salesData, searchFilters]);

    useEffect(() => {
        getProjects();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            const projectObj = Number(query.get('idProjects'))
                ? projects.find((item) => item.value === Number(query.get('idProjects')))
                : projects[0];

            setProject({
                label: projectObj?.label ?? 'Select Project',
                value: projectObj?.value ?? '',
            });
            setFormData((prev) => ({
                ...prev,
                idProjects: projectObj ? String(projectObj.value) : '',
            }));

            if (projectObj?.value) {
                getCategories(projectObj.value);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions = resp.data.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectOptions);
            })
            .catch((error) => console.error('Error fetching projects:', error));
    };

    const getCategories = async (idProjects: any) => {
        const url = process.env.REACT_APP_API_URL + 'allSrCategory?idProjects=' + idProjects;
        const response = await fetch(url, getRequestOptions());
        const data = await response.json();

        data.map((element: any) => {
            element.value = element.id_sr_categories;
            element.label = element.sr_category_name;
            return element;
        });

        const allCategoriesOpt = [{ id: '', name: '', label: 'All', value: '' }];
        const allCategories = data.concat(allCategoriesOpt);
        setCategories(allCategories);

        if (data.length === 1) {
            setCategory(data[0]);
            setFormData((prev) => ({ ...prev, idSrCategories: String(data[0].value) }));
            return;
        }

        const allOpt = allCategories.find((c: any) => c.value === '') || allCategoriesOpt[0];
        setCategory(allOpt);
        setFormData((prev) => ({ ...prev, idSrCategories: '' }));
    };

    const handleProjectChange = (selected: any) => {
        setProject(selected);
        setFormData((prev) => ({
            ...prev,
            idProjects: selected ? String(selected.value) : '',
            idSrCategories: '',
        }));
        if (selected?.value) {
            getCategories(selected.value);
        }
    };

    const handleCategoryChange = (selected: any) => {
        setCategory(selected);
        setFormData((prev) => ({
            ...prev,
            idSrCategories: selected ? String(selected.value) : '',
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearchFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setSearchFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleViewReport = async () => {
        if (!formData.idProjects) {
            toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
            return;
        }
        if (!formData.fromDate || !formData.toDate) {
            toast.error('Please select both From Date and To Date', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'mis-reports/daily-sales-report',
                postRequestOptions(formData)
            );
            const data = await response.json();
            if (data.success) {
                setSalesData(data.data);
                setSearchFilters(emptySearchFilters);
            } else {
                setSalesData(null);
                if (data.errors) {
                    for (const i in data.errors) {
                        toast.error(String(data.errors[i]), { position: 'bottom-right', autoClose: 4000 });
                    }
                } else {
                    toast.error(data.message ?? 'Failed to load report', { position: 'bottom-right', autoClose: 4000 });
                }
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            toast.error('Failed to load report', { position: 'bottom-right', autoClose: 4000 });
        } finally {
            setLoading(false);
        }
    };

    const formatQuantity = (quantity: number, unitName: string) => {
        const formattedQuantity = numberFormat(quantity);
        return unitName ? `${formattedQuantity} ${unitName}` : formattedQuantity;
    };

    const renderDateRows = (date: string, dateGroup: DailySalesDateGroup, dateIndex: number) => {
        const rows: React.ReactNode[] = [];
        let isFirstDateRow = true;

        dateGroup.products.forEach((product, productIndex) => {
            let isFirstProductRow = true;

            product.srs.forEach((sr, srIndex) => {
                let isFirstSrRow = true;

                sr.customers.forEach((customer, customerIndex) => {
                    rows.push(
                        <tr key={`${dateIndex}-${productIndex}-${srIndex}-${customerIndex}`}>
                            {isFirstDateRow && (
                                <td rowSpan={dateGroup.row_count}>{date}</td>
                            )}
                            {isFirstProductRow && (
                                <>
                                    <td rowSpan={product.row_count}>{product.product_name}</td>
                                    <td rowSpan={product.row_count}>{product.pack_size}</td>
                                </>
                            )}
                            <td className='text-end'>{formatQuantity(customer.quantity, customer.unit_name)}</td>
                            <td className='text-end'>{numberFormat(customer.total_ltr)}</td>
                            <td className='text-end'>{numberFormat(customer.invoice_price)}</td>
                            <td className='text-end'>{numberFormat(customer.total_amount)}</td>
                            <td className='text-end'>
                                {customer.free_product_quantity > 0
                                    ? formatQuantity(customer.free_product_quantity, customer.free_product_unit)
                                    : ''}
                            </td>
                            {isFirstSrRow && (
                                <td rowSpan={sr.row_count}>{sr.sr_name}</td>
                            )}
                            <td>{customer.customer_name}</td>
                        </tr>
                    );

                    isFirstDateRow = false;
                    isFirstProductRow = false;
                    isFirstSrRow = false;
                });
            });
        });

        if (dateGroup.row_count > 0) {
            rows.push(
                <tr key={`daily-total-${dateIndex}`}>
                    <td colSpan={4} className='text-end'><strong>Daily Total:</strong></td>
                    <td className='text-end'><strong>{numberFormat(dateGroup.total_ltr)}</strong></td>
                    <td></td>
                    <td className='text-end'><strong>{numberFormat(dateGroup.total_amount)}</strong></td>
                    <td colSpan={3}></td>
                </tr>
            );
        }

        return rows;
    };

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="category">
                                    <Form.Label>Select SR Category</Form.Label>
                                    <Select
                                        value={category}
                                        onChange={handleCategoryChange}
                                        options={categories}
                                        placeholder={category.label}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3">
                                    <Form.Label>From Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="From Date"
                                        name="fromDate"
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3">
                                    <Form.Label>To Date<span className='required'></span></Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="To Date"
                                        name="toDate"
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-4'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && <span className="spinner-border spinner-border-sm"></span>}
                                        <span><i className='fas fa-eye'></i> View Report</span>
                                    </button>
                                    <ExcelExportButton
                                        className='btn btn-sm btn-success p-3 ms-2'
                                        targetRef={reportRef}
                                        title='Daily Sales Report'
                                        filename={reportFileName('daily-sales-report', formData.fromDate, formData.toDate)}
                                        meta={reportMeta({ Project: chosen(project), 'SR Category': chosen(category), From: formData.fromDate, To: formData.toDate })}
                                        disabled={loading || !filteredSalesData}
                                    />
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12' ref={reportRef}>
                    {filteredSalesData ? (
                        <Table responsive striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Product Name</th>
                                    <th>Pack Size</th>
                                    <th>Quantity</th>
                                    <th>Total Ltr</th>
                                    <th>Invoice Price</th>
                                    <th>Total Amount</th>
                                    <th>Free Product</th>
                                    <th>SR Name</th>
                                    <th>Customer Name</th>
                                </tr>
                                <tr>
                                    <th>
                                        <Form.Control
                                            type="text"
                                            size="sm"
                                            name="date"
                                            placeholder="Search date"
                                            value={searchFilters.date}
                                            onChange={handleSearchFilterChange}
                                        />
                                    </th>
                                    <th>
                                        <Form.Control
                                            type="text"
                                            size="sm"
                                            name="productName"
                                            placeholder="Search product"
                                            value={searchFilters.productName}
                                            onChange={handleSearchFilterChange}
                                        />
                                    </th>
                                    <th>
                                        <Form.Control
                                            type="text"
                                            size="sm"
                                            name="packSize"
                                            placeholder="Search pack size"
                                            value={searchFilters.packSize}
                                            onChange={handleSearchFilterChange}
                                        />
                                    </th>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                    <th>
                                        <Form.Control
                                            type="text"
                                            size="sm"
                                            name="sr"
                                            placeholder="Search SR"
                                            value={searchFilters.sr}
                                            onChange={handleSearchFilterChange}
                                        />
                                    </th>
                                    <th>
                                        <Form.Control
                                            type="text"
                                            size="sm"
                                            name="customerName"
                                            placeholder="Search customer"
                                            value={searchFilters.customerName}
                                            onChange={handleSearchFilterChange}
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.keys(filteredSalesData.dates).map((date, dateIndex) => (
                                    <React.Fragment key={dateIndex}>
                                        {renderDateRows(date, filteredSalesData.dates[date], dateIndex)}
                                    </React.Fragment>
                                ))}
                                {Object.keys(filteredSalesData.dates).length > 0 && (
                                    <tr>
                                        <td colSpan={4} className='text-end'><strong>Grand Total:</strong></td>
                                        <td className='text-end'><strong>{numberFormat(filteredSalesData.grand_total_ltr)}</strong></td>
                                        <td></td>
                                        <td className='text-end'><strong>{numberFormat(filteredSalesData.grand_total_amount)}</strong></td>
                                        <td colSpan={3}></td>
                                    </tr>
                                )}
                                {Object.keys(filteredSalesData.dates).length === 0 && (
                                    <tr>
                                        <td colSpan={10} className='text-center'>No matching records found</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    ) : (
                        <div>No data available</div>
                    )}
                </div>
            </div>
        </>
    );
};

export default DailySalesReport;
